import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'oidc-client-ts';

import { buildLogoutUrl, getUserManager } from '@/auth/oidc';

export type AuthStatus = 'initialising' | 'authenticated' | 'anonymous' | 'error';

export interface AuthState {
  status: AuthStatus;
  /** Present only while `status` is `authenticated`. */
  user: User | null;
  /** Present only while `status` is `error`. */
  error: Error | null;
  /** Cognito subject claim. The server joins this to membership rows. */
  subject: string | null;
  email: string | null;
  /** Start the hosted Managed Login flow. `returnTo` survives the round trip. */
  signIn: (returnTo?: string) => void;
  /** Clear local state, then hand off to Cognito's logout endpoint. */
  signOut: () => void;
  /**
   * The bearer credential for the read API (plan 5.10), refreshed on demand.
   * Returns `null` when the session is gone, which callers surface as a
   * re-authentication prompt rather than as a failed request.
   */
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Key holding the in-app path to return to after the authorization hop. */
const RETURN_TO_KEY = 'zs.auth.returnTo';

function readClaim(user: User | null, claim: 'sub' | 'email'): string | null {
  const value = user?.profile?.[claim];
  return typeof value === 'string' ? value : null;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const manager = useMemo(() => getUserManager(), []);
  const [status, setStatus] = useState<AuthStatus>('initialising');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // A single in flight renew shared by every caller. Without this, a page that
  // mounts six queries at once fires six refresh token grants, and Cognito
  // rotates refresh tokens, so five of them race to invalidate the winner.
  const renewInFlight = useRef<Promise<User | null> | null>(null);

  const adopt = useCallback((next: User | null) => {
    if (next && !next.expired) {
      setUser(next);
      setError(null);
      setStatus('authenticated');
      return;
    }
    setUser(null);
    setStatus('anonymous');
  }, []);

  const renew = useCallback(async (): Promise<User | null> => {
    if (renewInFlight.current !== null) return renewInFlight.current;

    const attempt = (async (): Promise<User | null> => {
      try {
        return await manager.signinSilent();
      } catch {
        // An expired or revoked refresh token is an ordinary end of session,
        // not an application error. Drop the local record so the next render
        // shows the sign in screen instead of retrying forever.
        await manager.removeUser().catch(() => undefined);
        return null;
      } finally {
        renewInFlight.current = null;
      }
    })();

    renewInFlight.current = attempt;
    return attempt;
  }, [manager]);

  useEffect(() => {
    let cancelled = false;

    async function boot(): Promise<void> {
      try {
        const existing = await manager.getUser();
        if (cancelled) return;

        if (existing && !existing.expired) {
          adopt(existing);
          return;
        }
        if (existing?.refresh_token) {
          // The reload case: tokens were dropped with the tab's memory but the
          // refresh token survived in sessionStorage. Acceptance A3.11 is that
          // a hard refresh mid flow does not re-prompt for login.
          const renewed = await renew();
          if (!cancelled) adopt(renewed);
          return;
        }
        if (!cancelled) adopt(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
      }
    }

    void boot();

    const onLoaded = (next: User): void => adopt(next);
    const onUnloaded = (): void => adopt(null);
    const onSilentRenewError = (): void => {
      // Automatic renew has given up. Do not force a redirect from here: the
      // user may be mid keystroke. The next `getIdToken` call resolves null and
      // the screen that needs a token asks for a sign in.
      void manager.removeUser().catch(() => undefined);
    };

    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
      manager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [adopt, manager, renew]);

  const signIn = useCallback(
    (returnTo?: string) => {
      const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
      try {
        window.sessionStorage.setItem(RETURN_TO_KEY, target);
      } catch {
        // Losing the return path costs one extra navigation, not the session.
      }
      void manager.signinRedirect().catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
      });
    },
    [manager],
  );

  const signOut = useCallback(() => {
    void (async () => {
      // Clear the local record first. If the redirect is blocked, the tab must
      // still be signed out rather than showing a live dashboard.
      await manager.removeUser().catch(() => undefined);
      await manager.clearStaleState().catch(() => undefined);
      window.location.assign(buildLogoutUrl(window.location.origin));
    })();
  }, [manager]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    const current = await manager.getUser();
    if (current && !current.expired && current.id_token) return current.id_token;
    const renewed = await renew();
    return renewed?.id_token ?? null;
  }, [manager, renew]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      error,
      subject: readClaim(user, 'sub'),
      email: readClaim(user, 'email'),
      signIn,
      signOut,
      getIdToken,
    }),
    [status, user, error, signIn, signOut, getIdToken],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (ctx === null) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** Read and clear the path stashed before the authorization redirect. */
export function takeReturnTo(): string | null {
  try {
    const value = window.sessionStorage.getItem(RETURN_TO_KEY);
    window.sessionStorage.removeItem(RETURN_TO_KEY);
    return value;
  } catch {
    return null;
  }
}
